import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useCurrentUser } from '../../context/UserContext';
import {
  LucideIcon,
  LayoutDashboard,
  Users,
  FolderOpen,
  Video,
  MapPin,
  Menu,
  X,
  LogOut,
  ChevronRight,
  ClipboardList,
  Search,
  BarChart3,
  TrendingUp,
  Link as LinkIcon,
  Network,
  Database,
  UserCheck,
  Users2,
  CheckCheck,
  Activity,
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { signOut, user } = useAuthenticator();
  const navigate = useNavigate();
  const { role } = useCurrentUser();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // ── Build navigation items based on role ──────────────────────────────────

  const annotationItems: NavItem[] = [
    { to: '/annotation/tasks', label: 'Annotation Tasks', icon: ClipboardList },
  ];

  const supervisorItems: NavItem[] = [
    { to: '/annotation/tasks?tab=review', label: 'Review Queue', icon: Search },
  ];

  // Sub-admin sees only the final-review queue (filtered to 'approved' status)
  // and the analytics surfaces. No core admin pages.
  const subAdminItems: NavItem[] = [
    { to: '/annotation/tasks?tab=final-review', label: 'Final Review Queue', icon: CheckCheck },
  ];

  // Supervisor's own team-management surfaces (separate from admin assignments).
  const supervisorOwnItems: NavItem[] = [
    { to: '/supervisor/pending-approvals', label: 'Pending Approvals', icon: UserCheck },
    { to: '/supervisor/my-team', label: 'My Team', icon: Users2 },
  ];

  const adminCoreItems: NavItem[] = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/users', label: 'Users', icon: Users },
    { to: '/projects', label: 'Projects', icon: FolderOpen },
    { to: '/admin/languages', label: 'Submission Languages', icon: ClipboardList },
    { to: '/sessions', label: 'Sessions', icon: Video },
    { to: '/map', label: 'Map View', icon: MapPin },
  ];

  const adminAnnotationItems: NavItem[] = [
    { to: '/admin/assignments', label: 'Assignments', icon: LinkIcon },
    { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { to: '/admin/timebound-analytics', label: 'Timebound Analytics', icon: TrendingUp },
    { to: '/admin/supervisor-analytics', label: 'Supervisor Analytics', icon: Network },
    { to: '/admin/survey-analytics', label: 'Survey Analytics', icon: ClipboardList },
    { to: '/admin/data-export', label: 'Training Data Export', icon: Database },
  ];

  // ── Header text by role ───────────────────────────────────────────────────

  const headerText =
    role === 'enumerator'
      ? 'DataOne — Annotation Platform'
      : role === 'transcriber'
      ? 'DataOne — Transcriber Platform'
      : role === 'supervisor'
      ? 'DataOne — Supervisor Dashboard'
      : role === 'sub-admin'
      ? 'DataOne — Sub-Admin Console'
      : 'DataOne — Admin Panel';

  // ── Render a nav item ─────────────────────────────────────────────────────

  const renderNavItem = ({ to, label, icon: Icon }: NavItem) => (
    <NavLink
      key={to}
      to={to}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors group ${
          isActive
            ? 'bg-white/15 text-white'
            : 'text-brand-200 hover:bg-white/10 hover:text-white'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={18} />
          <span className="flex-1">{label}</span>
          {isActive && <ChevronRight size={14} className="opacity-60" />}
        </>
      )}
    </NavLink>
  );

  const renderSectionDivider = (label?: string) => (
    <div className="my-2 mx-3 border-t border-brand-700" key={`divider-${label}`}>
      {label && (
        <p className="text-brand-400 text-xs font-semibold uppercase tracking-widest mt-2 mb-1 px-0">
          {label}
        </p>
      )}
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-gradient-to-b from-brand-900 to-brand-800
          transform transition-transform duration-300 ease-in-out flex flex-col
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-brand-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <span className="text-brand-700 font-bold text-sm">D1</span>
            </div>
            <span className="text-white font-bold text-lg">DataOne</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-brand-300 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {/* Annotation Tasks — all roles */}
          {annotationItems.map(renderNavItem)}

          {/* My Statistics — transcribers + enumerators see their own
              recording / transcription volume. Higher roles already have
              the platform-wide Timebound Analytics page. */}
          {(role === 'transcriber' || role === 'enumerator') &&
            renderNavItem({
              to: '/my/stats',
              label: 'My Statistics',
              icon: Activity,
            })}

          {/* Review Queue — supervisor + admin */}
          {(role === 'supervisor' || role === 'admin') &&
            supervisorItems.map(renderNavItem)}

          {/* Final Review Queue — sub-admin + admin */}
          {(role === 'sub-admin' || role === 'admin') &&
            subAdminItems.map(renderNavItem)}

          {/* Supervisor's own team-management surfaces */}
          {role === 'supervisor' && (
            <>
              {renderSectionDivider()}
              {supervisorOwnItems.map(renderNavItem)}
            </>
          )}

          {/* Admin core pages */}
          {role === 'admin' && (
            <>
              {renderSectionDivider()}
              {adminCoreItems.map(renderNavItem)}
              {renderSectionDivider()}
              {adminAnnotationItems.map(renderNavItem)}
            </>
          )}

          {/* Supervisor analytics (but not full admin core) */}
          {role === 'supervisor' && (
            <>
              {renderSectionDivider()}
              {[
                { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
                { to: '/admin/timebound-analytics', label: 'Timebound Analytics', icon: TrendingUp },
                { to: '/admin/survey-analytics', label: 'Survey Analytics', icon: ClipboardList },
              ].map(renderNavItem)}
            </>
          )}

          {/* Sub-admin analytics (Supervisor + Timebound) */}
          {role === 'sub-admin' && (
            <>
              {renderSectionDivider()}
              {[
                { to: '/admin/supervisor-analytics', label: 'Supervisor Analytics', icon: Network },
                { to: '/admin/survey-analytics', label: 'Survey Analytics', icon: ClipboardList },
                { to: '/admin/timebound-analytics', label: 'Timebound Analytics', icon: TrendingUp },
              ].map(renderNavItem)}
            </>
          )}
        </nav>

        {/* User + Logout */}
        <div className="px-3 py-4 border-t border-brand-700">
          <div className="px-3 py-2 mb-2">
            <p className="text-brand-200 text-xs">Signed in as</p>
            <p className="text-white text-sm font-medium truncate">
              {user?.signInDetails?.loginId ?? 'User'}
            </p>
            {role && (
              <p className="text-brand-300 text-xs mt-0.5 capitalize">{role}</p>
            )}
          </div>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
              text-brand-200 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center px-4 lg:px-6 gap-4 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <Menu size={22} />
          </button>
          <div className="flex-1" />
          <div className="text-sm text-gray-500">{headerText}</div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
